import React, { FC, useState } from 'react';

// tslint:disable-next-line:max-line-length
import {
  FilterActionType,
  GroupActionType,
  GroupActionTypes,
  UserAction,
  UserActions,
} from '@go-shared/types/userActionRecord';
import {
  BubbleAccordion,
  BubbleDebounce,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import {
  FilterOptions,
  UserActionRecordUIFilters,
} from '@go-client/pages/UserActions/UsersActions';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { paginateEventTemplates } from '@go-client/lib/api/eventTemplate';
import { BubbleSearchSelect } from '@go-client/components/BubbleUI/BubbleSearchSelect';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { paginateVenueTemplates } from '@go-client/lib/api/venueTemplate';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { paginatePropertyContentTemplates } from '@go-client/lib/api/contentTemplate';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

type SelectOption = { label: string; value: string };
type ActionFilterOption = { value: FilterActionType; label: string };
type GroupActionFilterOption = { value: GroupActionType; label: string };

const actionOptions: Array<ActionFilterOption> = [
  { value: 'ALL', label: 'ALL' },
  { value: 'CREATED', label: 'Created' },
  { value: 'DELETED', label: 'Deleted' },
  { value: 'UPDATED', label: 'Updated' },
  { value: 'LINKED', label: 'Linked' },
  { value: 'UNLINKED', label: 'Unlinked' },
];

const subActionOpts: SelectOption[] = [
  { value: 'ALL', label: 'ALL' },
  ...UserActions.filter(ua => ua !== 'SILENT').map(ua => {
    return {
      label: ua,
      value: ua,
    };
  }),
];

const groupActionOpts: Array<GroupActionFilterOption> = [
  { value: 'ALL', label: 'ALL' },
  ...GroupActionTypes.filter(ua => ua !== 'SILENT').map(action => {
    return {
      label: action,
      value: action.toString(),
    };
  }),
];

interface UserRecordsSidebarProps {
  onFiltersChange: (filters: UserActionRecordUIFilters) => void;
  availableFiltersOptions: FilterOptions;
  activeFilters: UserActionRecordUIFilters;
}

export const UserActionsSidebar: FC<UserRecordsSidebarProps> = ({
  onFiltersChange,
  activeFilters,
  availableFiltersOptions,
}) => {
  const { brand } = useServerInfo();
  const { myProperties } = usePropertyInfo();
  const defaultSelectOption = {
    label: 'ALL',
    value: '',
  };

  const [action, setAction] = useState(
    actionOptions.find(opt => opt.value === activeFilters.action)
  );
  const [subAction, setSubAction] = useState<SelectOption>(defaultSelectOption);
  const [subActionOptions, setSubActionOptions] = useState<Array<SelectOption>>(subActionOpts);
  const [selectedProperty, setSelectedProperty] = useState<SelectOption>(defaultSelectOption);
  const [groupAction, setGroupAction] = useState<SelectOption>({ value: 'ALL', label: 'ALL' });

  const usersOpts = (availableFiltersOptions.users || []).map(user => {
    return {
      key: user._id,
      refKey: user._id,
      item: `${user.lastName}, ${user.firstName}`,
    };
  });

  const propertyOpts = [defaultSelectOption];
  (myProperties || []).map(property => {
    propertyOpts.push({
      label: property.name,
      value: property._id,
    });
  });

  const staffTemplateOpts = (availableFiltersOptions.staffTemplates || []).map(vt => {
    return {
      key: vt._id,
      refKey: vt._id,
      item: vt.title,
    };
  });

  const templateOpts = (availableFiltersOptions.templates || []).map(vt => {
    return {
      key: vt._id,
      refKey: vt._id,
      item: vt.name,
    };
  });

  const groupBookingOpts = (availableFiltersOptions.groupBookings || []).map(vt => {
    return {
      key: vt._id,
      refKey: vt._id,
      item: vt,
    };
  });

  const segmentsOpts = (brand.segments || []).map(s => {
    return {
      key: s._id,
      refKey: s._id,
      item: s.name,
    };
  });

  const levelsOpts = (brand.levels || []).map(lvl => {
    return {
      key: lvl._id,
      refKey: lvl._id,
      item: lvl.name,
    };
  });

  const attributesOpts = (brand.attributes || []).map(a => {
    return {
      key: a._id,
      refKey: a._id,
      item: a.name,
    };
  });

  const venutTypesOpts = (brand.venueTypes || []).map(vt => {
    return {
      key: vt._id,
      refKey: vt._id,
      item: vt.name,
    };
  });

  const mealPeriodsOpts = (brand.mealPeriods || []).map(mp => {
    return {
      key: mp._id,
      refKey: mp._id,
      item: mp.name,
    };
  });

  const dayThemeOpts = (brand.dayThemes || []).map(dt => {
    return {
      key: dt._id,
      refKey: dt._id,
      item: dt.name,
    };
  });

  const highlightsOpts = (brand.eventHighlights || []).map(eh => {
    return {
      key: eh._id,
      refKey: eh._id,
      item: eh.name,
    };
  });

  const commonTextsOpts = (brand.commonTexts || []).map(ct => {
    return {
      key: ct._id,
      refKey: ct._id,
      item: ct.name,
    };
  });

  const getEventTemplates = async (query: string) => {
    const { data } = await paginateEventTemplates({
      ...(activeFilters?.propertyId && { propertyId: activeFilters.propertyId }),
      search: query,
      perPage: 100,
    });
    return data;
  };

  const getVenueTemplates = async (query: string) => {
    const { data } = await paginateVenueTemplates({
      ...(activeFilters.propertyId && { propertyId: activeFilters.propertyId }),
      search: query,
      perPage: 100,
    });
    return data;
  };

  const getContentTemplates = async (query: string) => {
    const { data } = await paginatePropertyContentTemplates({
      ...(activeFilters.propertyId && { propertyId: activeFilters.propertyId }),
      search: query,
      perPage: 100,
    });
    return data;
  };

  const onActionChanged = (selected: ActionFilterOption) => {
    setAction(selected);
    setSubAction(defaultSelectOption);

    setSubActionOptions(
      selected.value !== 'ALL'
        ? [defaultSelectOption, ...subActionOpts.filter(so => so.value.includes(selected.value))]
        : subActionOpts
    );

    onFiltersChange({
      ...activeFilters,
      action: selected.value as FilterActionType,
      subAction: '',
    });
  };

  return (
    <>
      <BubbleAccordion
        variant="titanium"
        className="flex-grow flex flex-col border-t border-titanium-darkest"
        labels={['Basic Filters', 'Library', 'Translations', 'Templates', 'Group Bookings']}
        open={[0]}
      >
        <div className="grid gap-4 my-6">
          <BubbleFormGroup label="Action" labelClassName="text-white-i" className="px-4">
            <div className="flex items-start">
              <BubbleSelect
                block
                variant="titanium"
                allowUndefined={false}
                value={action}
                options={actionOptions}
                labelExtractor={opt => opt.label}
                onValue={onActionChanged}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Sub Action" labelClassName="text-white-i" className="px-4">
            <div className="flex items-start">
              <BubbleSelect
                block
                variant="titanium"
                allowUndefined={false}
                value={subAction}
                options={subActionOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setSubAction(selected);
                  onFiltersChange({
                    ...activeFilters,
                    page: 1,
                    subAction: selected.value as UserAction,
                  });
                }}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Group Actions" labelClassName="text-white-i" className="px-4">
            <div className="flex items-start">
              <BubbleSelect
                block
                variant="titanium"
                allowUndefined={false}
                value={groupAction}
                options={groupActionOpts}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setGroupAction(selected);
                  onFiltersChange({
                    ...activeFilters,
                    page: 1,
                    groupAction: selected.value,
                  });
                }}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Property" labelClassName="text-white-i" className="px-4">
            <div className="flex items-start">
              <BubbleSelect
                block
                variant="titanium"
                allowUndefined={false}
                value={selectedProperty}
                options={propertyOpts}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setSelectedProperty(selected);
                  onFiltersChange({
                    ...activeFilters,
                    page: 1,
                    propertyId: selected.value,
                  });
                }}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Users" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter users"
              menuClassName="max-h-80 overflow-y-auto"
              value={activeFilters.users}
              options={usersOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  users: [...activeFilters.users, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  users: activeFilters.users.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, users: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Dates" labelClassName="text-white-i" className="px-4">
            <div className="grid grid-cols-1 gap-1">
              <BubbleDebounce
                onTrigger={[
                  (date: string) => {
                    onFiltersChange({
                      ...activeFilters,
                      page: 1,
                      from: date,
                    });
                  },
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    variant="titanium"
                    type="date"
                    value={activeFilters.from}
                    onValue={debounce}
                  />
                )}
              </BubbleDebounce>
              <BubbleDebounce
                onTrigger={[
                  (date: string) => onFiltersChange({ ...activeFilters, page: 1, to: date }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    variant="titanium"
                    type="date"
                    value={activeFilters.to}
                    onValue={debounce}
                  />
                )}
              </BubbleDebounce>
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Archived" labelClassName="text-white-i" className="px-4">
            <div className="grid grid-cols-3 gap-1 align-left">
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.archived === undefined}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      archived: undefined,
                      page: 1,
                    })
                  }
                />
                <span>Any</span>
              </div>
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.archived === true}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      archived: true,
                      page: 1,
                    })
                  }
                />
                <span>Yes</span>
              </div>
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.archived === false}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      archived: false,
                      page: 1,
                    })
                  }
                />
                <span>No</span>
              </div>
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Locked" labelClassName="text-white-i" className="px-4">
            <div className="grid grid-cols-3 gap-1 align-left">
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.locked === undefined}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      locked: undefined,
                      page: 1,
                    })
                  }
                />
                <span>Any</span>
              </div>
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.locked === true}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      locked: true,
                      page: 1,
                    })
                  }
                />
                <span>Yes</span>
              </div>
              <div className="text-white relative">
                <BubbleInput
                  variant="titanium"
                  type="checkbox"
                  appearance="rounded"
                  inputClassName="stretched-link"
                  className="mr-1"
                  checked={activeFilters.locked === false}
                  onValue={() =>
                    onFiltersChange({
                      ...activeFilters,
                      locked: false,
                      page: 1,
                    })
                  }
                />
                <span>No</span>
              </div>
            </div>
          </BubbleFormGroup>
        </div>

        <div className="grid gap-4 my-6">
          <BubbleFormGroup label="Venues" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<VenueTemplate>
              block
              variant="titanium"
              fetchOptions={getVenueTemplates}
              renderItem={vt => <>{vt.name}</>}
              getItemValue={vt => vt.name}
              onItemSelected={vt => {
                onFiltersChange({
                  ...activeFilters,
                  venueTemplateId: vt ? [vt._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by venue template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Events" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<EventTemplate>
              block
              variant="titanium"
              fetchOptions={getEventTemplates}
              renderItem={et => <>{et.title}</>}
              getItemValue={et => et.title}
              onItemSelected={et => {
                onFiltersChange({
                  ...activeFilters,
                  eventTemplateId: et ? [et._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by event template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Content" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<BareContentTemplate>
              block
              variant="titanium"
              fetchOptions={getContentTemplates}
              renderItem={ct => <>{ct.name}</>}
              getItemValue={ct => ct.name}
              onItemSelected={ct => {
                onFiltersChange({
                  ...activeFilters,
                  contentTemplateId: ct ? [ct._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by content template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Staff" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by staff title"
              menuClassName="max-h-80 overflow-y-auto"
              value={activeFilters.staffTemplateId}
              options={staffTemplateOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  staffTemplateId: [...activeFilters.staffTemplateId, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  staffTemplateId: activeFilters.staffTemplateId.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, staffTemplateId: [], page: 1 });
              }}
            />
          </BubbleFormGroup>
        </div>

        <div className="grid gap-4 my-6">
          <BubbleFormGroup label="Venues" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<VenueTemplate>
              block
              variant="titanium"
              fetchOptions={getVenueTemplates}
              renderItem={vt => <>{vt.name}</>}
              getItemValue={vt => vt.name}
              onItemSelected={vt => {
                onFiltersChange({
                  ...activeFilters,
                  refersVenueTemplateId: vt ? [vt._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by venue template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Events" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<EventTemplate>
              block
              variant="titanium"
              fetchOptions={getEventTemplates}
              renderItem={et => <>{et.title}</>}
              getItemValue={et => et.title}
              onItemSelected={et => {
                onFiltersChange({
                  ...activeFilters,
                  refersEventTemplateId: et ? [et._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by event template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Content" labelClassName="text-white-i" className="px-4">
            <BubbleSearchSelect<BareContentTemplate>
              block
              variant="titanium"
              fetchOptions={getContentTemplates}
              renderItem={ct => <>{ct.name}</>}
              getItemValue={ct => ct.name}
              onItemSelected={ct => {
                onFiltersChange({
                  ...activeFilters,
                  refersContentTemplateId: ct ? [ct._id] : [],
                  page: 1,
                });
              }}
              placeholder="Filter by content template"
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Segments" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by segments"
              value={activeFilters.segments}
              options={segmentsOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  segments: [...activeFilters.segments, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  segments: activeFilters.segments.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, segments: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="levels" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by levels"
              value={activeFilters.levels}
              options={levelsOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  levels: [...activeFilters.levels, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  levels: activeFilters.levels.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, levels: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="attributes" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by attributes"
              value={activeFilters.attributes}
              options={attributesOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  attributes: [...activeFilters.attributes, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  attributes: activeFilters.attributes.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, attributes: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="venue types" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by venue types"
              value={activeFilters.venueTypes}
              options={venutTypesOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  venueTypes: [...activeFilters.venueTypes, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  venueTypes: activeFilters.venueTypes.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, venueTypes: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="meal periods" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by meal periods"
              value={activeFilters.mealPeriods}
              options={mealPeriodsOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  mealPeriods: [...activeFilters.mealPeriods, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  mealPeriods: activeFilters.mealPeriods.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, mealPeriods: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="day themes" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by day themes"
              value={activeFilters.dayThemes}
              options={dayThemeOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  dayThemes: [...activeFilters.dayThemes, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  dayThemes: activeFilters.dayThemes.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, dayThemes: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="highlights" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by highlights"
              value={activeFilters.highlights}
              options={highlightsOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  highlights: [...activeFilters.highlights, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  highlights: activeFilters.highlights.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, highlights: [], page: 1 });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="common texts" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              variant="titanium"
              placeholder="Filter by common texts"
              value={activeFilters.commonTexts}
              options={commonTextsOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  commonTexts: [...activeFilters.commonTexts, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  commonTexts: activeFilters.commonTexts.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, commonTexts: [], page: 1 });
              }}
            />
          </BubbleFormGroup>
        </div>

        <div className="grid gap-4 my-6">
          <BubbleFormGroup label="Templates" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              readOnly={!activeFilters.propertyId}
              readOnlyLabel="Select a property first"
              variant="titanium"
              placeholder="Filter by template name"
              menuClassName="max-h-80 overflow-y-auto"
              value={activeFilters.templateId}
              options={templateOpts}
              labelExtractor={l => l}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  templateId: [...activeFilters.templateId, v],
                  page: 1,
                });
              }}
              onRemove={v => {
                onFiltersChange({
                  ...activeFilters,
                  templateId: activeFilters.templateId.filter(v2 => v2.key !== v.key),
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({ ...activeFilters, templateId: [], page: 1 });
              }}
            />
          </BubbleFormGroup>
        </div>

        <div className="grid gap-4 my-6">
          <BubbleFormGroup label="Group Bookings" labelClassName="text-white-i" className="px-4">
            <BubbleTypeahead
              block
              readOnly={!activeFilters.propertyId}
              readOnlyLabel="Select a property first"
              variant="titanium"
              placeholder="Filter by booking group name"
              menuClassName="max-h-80 overflow-y-auto"
              value={activeFilters.groupBookingId}
              options={groupBookingOpts}
              labelExtractor={l => l.party.name}
              onAdd={v => {
                onFiltersChange({
                  ...activeFilters,
                  groupBookingId: [...activeFilters.groupBookingId, v],
                  groupBookingPartyId: [...activeFilters.groupBookingPartyId, v.item.party.name],
                  page: 1,
                });
              }}
              onRemove={v => {
                const idx = activeFilters.groupBookingPartyId.findIndex(
                  gb => gb === v.item.party.name
                );
                if (idx < 0) return;
                const newGroupBookingParty = [...activeFilters.groupBookingPartyId];
                newGroupBookingParty.splice(idx, 1);

                onFiltersChange({
                  ...activeFilters,
                  groupBookingId: activeFilters.groupBookingId.filter(v2 => v2.key !== v.key),
                  groupBookingPartyId: newGroupBookingParty,
                  page: 1,
                });
              }}
              onClearAll={() => {
                onFiltersChange({
                  ...activeFilters,
                  groupBookingId: [],
                  groupBookingPartyId: [],
                  page: 1,
                });
              }}
            />
          </BubbleFormGroup>
        </div>
      </BubbleAccordion>
    </>
  );
};
